import { useMemo, useState } from 'react';
import sortBy from 'lodash/sortBy';

import { ComponentResponse } from 'approot/activity-builder/components/component.constants';
import { ErrorMessageActionable } from 'approot/shared/error-message/error-message-actionable';
import Bouncer from 'approot/shared/bouncer/bouncer';
import { ResponseViewContainer } from 'approot/activity-builder/components/response/response-view.style';
import { useActivityBuilderPresentContext } from 'approot/activity-builder/present/activity-builder-present.context';
import { useResponseBaseHeight } from 'approot/activity-builder/components/response/response.hooks';
import { useGetStudentAnswersPollingFallback } from '../use-get-student-answers';
import { useGetClassStudents } from '../classroom-student.hooks';
import SubActivityView from 'approot/activity-builder/components/sub-activity/sub-activity-view';
import ResponseFieldReadonly from 'approot/activity-builder/components/response/response-field-readonly';
import {
  ClassroomAnswerGrid,
  ClassroomAnswerGridButton,
  ClassroomAnswerGridInner,
  ClassroomAnswerGridItem,
  ClassroomAnswerInstruction,
} from '../classroom-answer.style';
import { ClassroomStudentIdentifierWithVisibility } from '../classroom-student-identifier';
import { ClassAnswerNoStudents } from '../class-answer-empty';
import {
  getStudentResponseAnswers,
  hasStudentResponseAnswer,
} from './text-response-individual.utils';
import ClassroomAwaitingResponse from '../classroom-awaiting';
import { ScreenReaderOnly } from 'approot/shared/styles/global.styles';
import ActivityBuilderComponentPreview from 'approot/activity-builder/page/activity-builder-component-preview';
import { useFindComponentDimensions } from 'approot/activity-builder/components/component.hooks';
import { COMPONENT_SELECTOR } from 'approot/activity-builder/components/shared/logic/get-component-selector-id';
import ClassroomAnswerDetailHeader from '../classroom-answer-detail-header';
import { ActivitySheetInnerMax } from 'approot/activity-builder/components/shared/sheet/activity-sheet.style';
import ClassroomAnswerStudentVisibility from '../classroom-answer-student-visibility';
import { Box } from 'approot/shared/design-system/box/box.style';

type Props = {
  component: Omit<ComponentResponse, '__typename'>;
};

const TextResponseIndividual = ({ component }: Props) => {
  const [activeStudent, setActiveStudent] = useState<number>(0);

  const { userClassId } = useActivityBuilderPresentContext();
  const { width } = useFindComponentDimensions(
    component.id,
    COMPONENT_SELECTOR
  );

  const baseHeight = useResponseBaseHeight();

  const {
    data: studentsData,
    loading: studentsLoading,
    error: studentsError,
  } = useGetClassStudents({
    classId: Number(userClassId),
  });

  const { data, error: studentAnswersError } =
    useGetStudentAnswersPollingFallback({
      userClassId: Number(userClassId),
      componentId: component.id,
    });

  const sortedFields = useMemo(
    () => sortBy(component.properties.textResponse.fields, 'sort'),
    [component.properties.textResponse.fields]
  );

  const studentsInClass = useMemo(
    () => studentsData?.getClassStudents || [],
    [studentsData]
  );

  const studentAnswers = useMemo(
    () => data?.getClassStudentsAnswers || [],
    [data]
  );

  const results = useMemo(() => {
    const students =
      activeStudent > 0
        ? studentsInClass.filter(s => s.id === activeStudent)
        : studentsInClass;

    return getStudentResponseAnswers({
      sortedFields,
      studentAnswers,
      studentsInClass: students,
    });
  }, [activeStudent, studentsInClass, studentAnswers, sortedFields]);

  if (studentsLoading || !studentAnswers) {
    return <Bouncer />;
  }

  if (studentAnswersError || studentsError) {
    return <ErrorMessageActionable ariaPage="Text responses" paddingTop={0} />;
  }

  if (studentsInClass.length === 0) {
    return <ClassAnswerNoStudents />;
  }

  return (
    <ClassroomAnswerInstruction>
      {activeStudent > 0 ? (
        <ActivitySheetInnerMax>
          <ClassroomAnswerDetailHeader
            studentId={activeStudent}
            students={studentsInClass}
            onSelect={setActiveStudent}
          />
          <SubActivityView subActivityDetails={component.subActivityProperties}>
            {hasStudentResponseAnswer(results[0]) ? (
              <ResponseViewContainer>
                {sortedFields.map((field, index) => (
                  <ResponseFieldReadonly
                    key={field.itemId}
                    baseHeight={baseHeight}
                    field={field}
                    answer={results[0].answers[index]?.answer}
                  />
                ))}
              </ResponseViewContainer>
            ) : (
              <ClassroomAwaitingResponse />
            )}
          </SubActivityView>
        </ActivitySheetInnerMax>
      ) : (
        <SubActivityView subActivityDetails={component.subActivityProperties}>
          <Box $justifyContent="flex-end" $marginBottom={24}>
            <ClassroomAnswerStudentVisibility />
          </Box>
          <ClassroomAnswerGrid>
            {results.map(result => (
              <ClassroomAnswerGridItem key={result.studentId}>
                <ClassroomAnswerGridInner>
                  {hasStudentResponseAnswer(result) ? (
                    <>
                      <ClassroomAnswerGridButton
                        type="button"
                        onClick={() => setActiveStudent(result.studentId)}
                      >
                        <ScreenReaderOnly>
                          {`View ${result.studentName}'s response`}
                        </ScreenReaderOnly>
                      </ClassroomAnswerGridButton>
                      <ActivityBuilderComponentPreview
                        content={{
                          width,
                          height: 232,
                        }}
                      >
                        <ResponseViewContainer $maxWidth={width}>
                          {sortedFields.map((field, index) => (
                            <ResponseFieldReadonly
                              key={field.itemId}
                              baseHeight={baseHeight}
                              field={field}
                              answer={result.answers[index]?.answer}
                            />
                          ))}
                        </ResponseViewContainer>
                      </ActivityBuilderComponentPreview>
                    </>
                  ) : (
                    <ClassroomAwaitingResponse />
                  )}
                </ClassroomAnswerGridInner>
                <ClassroomStudentIdentifierWithVisibility
                  name={result.studentName}
                  emoji={result.studentEmoji}
                />
              </ClassroomAnswerGridItem>
            ))}
          </ClassroomAnswerGrid>
        </SubActivityView>
      )}
    </ClassroomAnswerInstruction>
  );
};

export default TextResponseIndividual;
